import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import type { MouseEvent, ReactNode } from "react";
import { Link as RouterLink, type To } from "react-router";

/**
 * The part of a row that says the row opens something.
 *
 * Rows that open a record were marked as clickable with `cursor: pointer` and nothing else, which
 * is a signal you have to already be hovering to receive. #5122 is what that costs: a user looked
 * at a row with a delete icon on it, found no edit icon beside the delete, and concluded the entry
 * could not be edited — while the row under their cursor opened the editor. The icon column was
 * where they looked because nothing in the row itself said *click me*.
 *
 * The answer is not an edit icon. That would duplicate the row gesture exactly, on the tightest
 * column in the table, and it would do nothing for the eight tables that are clickable and have no
 * actions column at all. Naming the row as a link says the same thing in the place the eye already
 * is, costs no column width, and gives keyboard and screen-reader users a target that a hover
 * cursor never did.
 *
 * ### Underlined at rest, not on hover
 *
 * Hover-only underlining reproduces the bug in a nicer font: the affordance is still invisible
 * until you are already pointing at it. Underlined at rest is also what the app already does in
 * the two places a table cell was hand-rolled into a link — `grantee-name-cell` and
 * `fee-schedule-template-link` — so this is the existing answer rather than a third one.
 *
 * ### A route gets an anchor, a dialog gets a button
 *
 * Pass `to` where the row navigates and the link is a real `<a href>`: middle-click, ctrl-click and
 * "open in new tab" all work, which they never did on a row `onClick`. Pass `onClick` where the row
 * opens a dialog and there is no URL to point at; that renders a button styled the same way, which
 * is focusable and keyboard-operable even though it has nowhere to point. Passing neither renders
 * plain text, which is what a row that cannot be opened — a load issue, a skeleton — should get.
 */
const RowLinkText = styled(Link)(({ theme }) => ({
  // `component="button"` renders a real <button>, which arrives with a user-agent font, padding and
  // border that a table cell should not inherit. Stating them here means the anchor and the button
  // are the same text at the same baseline, which is the whole point of having one component.
  background: "none",
  border: "none",
  color: theme.palette.text.primary,
  cursor: "pointer",
  display: "block",
  font: "inherit",
  maxWidth: "100%",
  overflow: "hidden",
  padding: 0,
  textAlign: "left",
  textDecorationColor: theme.palette.text.primary,
  textOverflow: "ellipsis",
  verticalAlign: "baseline",
  whiteSpace: "nowrap",
})) as typeof Link;

export default function RowLink(
  props: Readonly<{
    children: ReactNode;
    /** Where the row navigates. Renders a real anchor, so the browser's own link gestures work. */
    to?: To;
    /** What the row opens, where it opens a dialog rather than a route. Renders a button. */
    onClick?: () => void;
    /**
     * Accessible name, where the visible text is not enough on its own — a column of links reading
     * "Untitled" tells a screen reader nothing about which row it is on. Defaults to the text.
     */
    ariaLabel?: string;
  }>,
) {
  const { ariaLabel, children, onClick, to } = props;

  // A row that cannot be opened must not claim it can. Load-issue and skeleton rows reach here with
  // neither destination, and get the text they would have had before this component existed.
  if (to === undefined && onClick === undefined) return <>{children}</>;

  // The row underneath is clickable too and goes to the same place. Letting both fire opens the
  // record twice — harmless for a route, two stacked dialogs for the rest.
  function handleClick(event: MouseEvent) {
    event.stopPropagation();
    onClick?.();
  }

  if (to !== undefined) {
    return (
      <RowLinkText aria-label={ariaLabel} component={RouterLink} to={to} onClick={handleClick}>
        {children}
      </RowLinkText>
    );
  }

  return (
    <RowLinkText aria-label={ariaLabel} component="button" type="button" onClick={handleClick}>
      {children}
    </RowLinkText>
  );
}
